/**
 * Non-gameplay preferences (tech-design §5 settings store, GAM-25). Persisted in the
 * IndexedDB settings store — separate from saves, so wiping or transferring a save
 * never touches the player's preferences. Imports `@core` types only (layer rules).
 */
import type { Notation } from '@core/index.js';
import { getSetting, setSetting } from './db.js';

/** Particle/effect quality tier — mirrors `@render`'s `ParticleTier` values (the
 * string union is duplicated because persistence may not import render, lint-enforced). */
export type ParticleQuality = 'off' | 'low' | 'medium' | 'high';

export interface Settings {
  readonly notation: Notation;
  /** Particle/effect quality — effects degrade first under the perf budget (§8). */
  readonly particleQuality: ParticleQuality;
  /** Autosave interval in seconds (clamped to 5–300 by the UI). */
  readonly autosaveInterval: number;
  /** Honor prefers-reduced-motion / disable non-essential animation (accessibility). */
  readonly reducedMotion: boolean;
  /** High-contrast palette for the DOM overlay. */
  readonly highContrast: boolean;
  /** Which save slot the game loop reads and writes. */
  readonly activeSlot: string;
}

export const DEFAULT_SETTINGS: Settings = {
  notation: 'letters',
  particleQuality: 'medium',
  autosaveInterval: 30,
  reducedMotion: false,
  highContrast: false,
  activeSlot: 'slot-1',
};

const SETTINGS_KEY = 'settings';

/** Load persisted settings, filling gaps with defaults (forward-compatible). */
export async function loadSettings(): Promise<Settings> {
  const stored = await getSetting<Partial<Settings>>(SETTINGS_KEY);
  return { ...DEFAULT_SETTINGS, ...(stored ?? {}) };
}

/** Merge a partial update into the persisted settings and return the result. */
export async function saveSettings(update: Partial<Settings>): Promise<Settings> {
  const next = { ...(await loadSettings()), ...update };
  await setSetting(SETTINGS_KEY, next);
  return next;
}
